import { ApplicationConfig, ErrorHandler, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { routes } from './app.routes';
import { AuthenticationService } from '../services/authentication.service';
import { NoCacheInterceptor } from '../services/no-cache.interceptor';
import { ChunkErrorHandler } from '../services/chunk-error-handler.service';
import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptor
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';

export function loggerCallback(logLevel: LogLevel, message: string) {
  // MSAL logging callback (logs disabled for production)
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureClientId,
      authority: 'https://login.microsoftonline.com/2ede383a-7e1f-4357-a846-85886b2c0c4d',
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowRedirectInIframe: false,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [environment.azureApiScope], // Use the same scope as old app
    },
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  protectedResourceMap.set(environment.awsApiUrlTechref, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlAssetAnnually, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlPortal, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlDashboard, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlDeleteIndicator, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlDad, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlSADM, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlDataQuality, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlTransformerAnalytics, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlAiRag, [environment.azureApiScope]);
  protectedResourceMap.set(environment.awsApiUrlCostOptimizer, [environment.azureApiScope]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function initializeMsal(msalService: MsalService, authService: AuthenticationService) {
  return () => {
    return msalService.instance
      .initialize()
      .then(() => {
        return msalService.instance.handleRedirectPromise();
      })
      .then((result) => {
        // ALWAYS check for active account, whether redirect or cached
        authService.checkAndSetActiveAccount();

        // Check if there's an error in the URL hash
        const urlParams = new URLSearchParams(window.location.hash.substring(1));
        const error = urlParams.get('error');

        if (error) {
          // Clear the hash to prevent loop
          window.history.replaceState(null, '', window.location.pathname);

          // Don't block initialization
          return Promise.resolve();
        }

        return Promise.resolve();
      })
      .catch(() => {
        // Clear any bad state
        authService.reportStartupFailure();
        return Promise.resolve();
      });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    {
      provide: ErrorHandler,
      useClass: ChunkErrorHandler,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService, AuthenticationService],
      multi: true,
    },
  ],
};
